import React, { useContext, useEffect, useRef, useState } from 'react';
import { IGif } from '@giphy/js-types';
import { Stack, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Editor } from '@tinymce/tinymce-react';
import { useCreateDownloads } from '@juno/client-api';
import { PreviewFile } from '@juno/client-api/fakeModel';
import { CommunityUser, Download, Tag, Thread } from '@juno/client-api/model';
import { AutocompleteTagsGeneric, DrawerFooter } from '@juno/ui';
import {
  MutationAction,
  onMutation,
  uploadImagesToCloudinary,
  uploadTinyMceImageCloudinary,
  useSettings,
} from '@juno/utils';
import { useS3Upload } from '../../Contexts/S3Context';
import { TagsContext } from '../../Feed';
import CommentEditor from '../CommentsRepliesSection/CommentEditor';
import { createTinyMceMentionSpan, mentions_fetch } from '../helpers';
import EditorActionMenu from './EditorActionMenu';
import './styles.css';

interface ThreadEditorProps {
  threadToEdit: Thread;
  handleUpdate: (updatedThread: Thread) => void;
  handleCancel?: () => void;
  saveBtnText?: string;
  showActionMenu?: boolean;
  showToolbar?: boolean;
  refetch?: () => void;
}

const ThreadEditor: React.FC<ThreadEditorProps> = ({
  threadToEdit,
  handleUpdate,
  handleCancel,
  saveBtnText,
  showActionMenu = true,
  showToolbar = true,
  refetch,
}) => {
  const {
    title,
    body,
    image,
    downloads,
    tagged_users,
    comment_media,
    comment_media_type,
    post_video,
  } = threadToEdit;

  const { getImageSrc, isSmallScreen, site, platform } = useSettings();
  const theme = useTheme();

  const tags = useContext(TagsContext);
  const isReply = !!threadToEdit.parent_thread_id;

  // ! Can I check if the url has 'cloudinary' in it to determine if this is an image vs a video? Images get uploaded to cloudinary, videos go to S3...
  const isImage = image ? image.search('cloudinary') >= 0 : false;
  const [previewMedia, setPreviewMedia] = useState<PreviewFile[]>([
    { type: isImage ? 'image' : 'video', preview: image || '' } as PreviewFile,
  ]);
  const [previewVideo, setPreviewVideo] = useState<PreviewFile | string>(post_video || '');

  const [commentPreviewMedia, setCommentPreviewMedia] = useState<PreviewFile | null>(null);
  const [gifMedia, setGifMedia] = useState<IGif | null>(null);
  const [linkMedia, setLinkMedia] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [removedFiles, setRemovedFiles] = useState<Download[]>([]);
  const [dirty, setDirty] = useState(false);
  const [editorHasContent, setEditorHasContent] = useState(!!body);
  const [tagsState, setTagsState] = useState<Tag[]>(threadToEdit.tags ?? []);
  const [titleContent, setTitleContent] = useState(title);

  const editorRef = useRef<any>(null);
  const userMentions = useRef<CommunityUser[]>(tagged_users ?? []);

  const createDownloads = useCreateDownloads(onMutation(MutationAction.CREATE, ''));
  const { uploadFileToS3 } = useS3Upload();

  useEffect(() => setDirty(true), [newFiles, titleContent, tagsState, previewVideo]);
  useEffect(() => setDirty(false), [body]);

  const handleEditorChange = (content: string) => {
    setDirty(true);
    setEditorHasContent(content.trim().length > 0);
  };

  const isUploadedVideo = () => {
    if (
      typeof previewVideo == 'string' &&
      !previewVideo.toLowerCase().includes('youtube') &&
      !previewVideo.toLowerCase().includes('youtu.be') &&
      !previewVideo.toLowerCase().includes('vimeo')
    ) {
      return true;
    }
    return false;
  };

  const canUploadVideo = () => {
    if (typeof previewVideo !== 'string' && previewVideo.preview !== '') {
      return true;
    }
    return false;
  };
  const handleSubmitChanges = async () => {
    if (!editorHasContent) {
      alert('Please enter some text before submitting');
      return;
    }

    const values = {
      body: editorRef.current?.getContent(),
      title: titleContent,
    } as Thread;
    try {
      const promises = [
        ...newFiles.map((file) => uploadFileToS3(file, setUploadProgress, true, `post_files`)),
      ];
      const urls = await Promise.all(promises);
      const files = newFiles.map(
        (file, index) => ({ title: file.name, url: urls[index], filetype: file.type } as Download),
      );
      if (canUploadVideo()) {
        const videoURL = await uploadFileToS3(
          previewVideo as PreviewFile,
          setUploadProgress,
          true,
          'post_files',
        );
        values.post_video = videoURL;
      } else {
        values.post_video = previewVideo as string;
      }
      if (files.length > 0) {
        const newFileDownloads = await createDownloads.mutateAsync({
          siteId: site?.id || '',
          data: files,
        });
        values.downloads = [...(downloads ?? []), ...newFileDownloads];
      }

      if (previewMedia?.[0]?.lastModified || previewMedia?.[0]?.forceAppUpload) {
        const file = previewMedia[0];
        try {
          if (file.type.search('image') >= 0) {
            const newImage = await uploadImagesToCloudinary(previewMedia, setUploadProgress);
            values.image = newImage.url;
            values.metadata = {
              image_metadata: newImage,
            };
          } else if (file.type.search('video') >= 0) {
            const newVideo = await uploadFileToS3(file, setUploadProgress, true, `post_files`);
            values.image = newVideo;
            values.metadata = {
              video_metadata: newVideo,
            };
          } else {
            alert('Unsupported file type');
            return;
          }
        } catch (e) {
          console.error(e);
          return;
        }
      } else if (previewMedia.length === 0) {
        values.image = '';
        values.metadata = {
          image_metadata: null,
        };
      }

      if (removedFiles.length > 0) {
        values.downloads = downloads?.filter((d) => !removedFiles.some((r) => r.id === d.id));
      }

      // double check that the user mentions are still in the body
      values.tagged_users = userMentions.current?.filter((m) =>
        editorRef.current?.getContent().includes(m.id),
      );

      values.tags = tagsState;

      handleUpdate(values);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (isUploadedVideo()) {
      setPreviewVideo('');
    }
  }, []);

  return (
    <>
      <Stack spacing={2}>
        {!isReply && (
          <>
            <TextField
              fullWidth
              value={titleContent}
              onChange={(e) => setTitleContent(e.target.value)}
              name='Post Title'
              label='Post Title (optional)'
              aria-label='Post Title'
              placeholder='Add Title...'
              variant='outlined'
              size='small'
              margin='dense'
            />
            <Editor
              apiKey={process.env['NX_TINY_MCE_API_KEY']}
              onInit={(evt, editor) => (editorRef.current = editor)}
              initialValue={body as unknown as string}
              onDirty={() => setDirty(true)}
              onEditorChange={handleEditorChange}
              init={{
                convert_urls: false,
                contextmenu: false,
                auto_focus: true,
                placeholder: 'Add Content...',
                content_style: `.user_mention{ color: ${theme.palette.primary.main};, cursor: pointer; }`,
                mentions_selector: 'span.user_mention',
                mentions_menu_complete: (editor: any, userInfo: any) => {
                  userMentions.current = [...userMentions.current, userInfo];
                  const span = createTinyMceMentionSpan(editor, userInfo);
                  return span;
                },
                mentions_fetch: (query: any, success: any) => {
                  mentions_fetch(query, success, getImageSrc, platform?.id || '');
                },
                mentions_item_type: 'profile',
                height: isSmallScreen ? 300 : 525,
                images_upload_handler: uploadTinyMceImageCloudinary,
                setup: (editor) => {
                  editor.setProgressState(true);
                },
                toolbar: showToolbar
                  ? 'emoticons bold italic underline strikethrough link | alignleft aligncenter alignright alignjustify outdent indent | bullist numlist blockquote'
                  : false,
                toolbar_location: 'bottom',
                menubar: false,
                statusbar: false,
                skin: 'material-outline',
                highlight_on_focus: false,
              }}
              plugins={'autolink emoticons lists link mentions'}
            />
          </>
        )}
        {isReply && (
          <CommentEditor
            onCancel={handleCancel}
            userMentions={userMentions}
            editorRef={editorRef}
            thread={threadToEdit}
            parentThreadId=''
            onSubmitSuccess={() => {
              refetch && refetch();
              handleCancel && handleCancel();
            }}
          />
        )}

        {showToolbar && tags && tags?.length > 0 && (
          <AutocompleteTagsGeneric
            tags={tagsState || []}
            setTags={(tags) => setTagsState(tags)}
            siteId={site?.id || ''}
            label='Tags'
            popperPlacement='top'
          />
        )}
      </Stack>
      {showActionMenu && (
        <EditorActionMenu
          previewMedia={previewMedia}
          setPreviewMedia={setPreviewMedia}
          newFiles={newFiles}
          setNewFiles={setNewFiles}
          existingFiles={downloads}
          setRemovedFiles={(file: Download) => {
            const removed = downloads?.filter((f) => f.id === file.id || f.title === file.title);
            if (!removed) return;
            setRemovedFiles([...removedFiles, ...removed]);
          }}
          previewVideo={previewVideo}
          setPreviewVideo={setPreviewVideo}
        />
      )}

      {handleCancel && !isReply && (
        <DrawerFooter
          confirmButtonText='Post'
          onSubmit={() => {
            setUploadProgress(1);
            handleSubmitChanges();
          }}
          onCancel={handleCancel}
          disabled={!dirty || !editorHasContent}
          submitting={uploadProgress > 0}
        />
      )}
    </>
  );
};
export default ThreadEditor;
